import { getVoucherRooms, Room } from '@/services/cms'
import { getPlanDetail, getPlanList } from '@/services/market'
import type { PlanDetails, PlanList } from '@/types/market'
import { useLocale } from 'next-intl'
import useSWR from 'swr'

type PremiumPlansResult = {
  planList?: PlanList | null
  planDetails?: PlanDetails | null
  voucherId?: string
  isLoading: boolean
  isEmpty: boolean
  isError: boolean
}

type PremiumPlanVoucherRoomsResult = {
  voucherRooms?: Room[]
  isVoucherRoomsLoading?: boolean
  isVoucherRoomsErr?: boolean
  isVoucherRoomsEmpty?: boolean
}

export const usePremiumPlanVoucherRooms = (
  voucherId?: string,
  enabled?: boolean,
  sessionKey?: string,
): PremiumPlanVoucherRoomsResult => {
  const {
    data: voucherRooms = [],
    isLoading: isVoucherRoomsLoading,
    error: voucherRoomsErr,
  } = useSWR(
    voucherId && enabled && sessionKey
      ? (['getVoucherRooms', voucherId, sessionKey] as const)
      : null,
    async ([, id]) => {
      const res = await getVoucherRooms(id)
      if (!res) throw new Error('voucherRooms request failed')
      const rooms = res.results?.results
      return Array.isArray(rooms) ? (rooms as Room[]) : []
    },
    {
      revalidateIfStale: false,
      revalidateOnMount: true,
      revalidateOnFocus: false,
    },
  )
  const voucherRoomsEmpty = voucherRooms.length === 0
  return {
    voucherRooms,
    isVoucherRoomsLoading,
    isVoucherRoomsErr: voucherRoomsErr,
    isVoucherRoomsEmpty: voucherRoomsEmpty,
  }
}

export const getPlanIds = (planList?: PlanList | null): string | null => {
  const items = planList?.data?.products?.items
  if (!Array.isArray(items) || items.length === 0) return null
  const ids = items.map((item) => String(item?.id ?? '')).filter((id) => id !== '')
  return ids.length > 0 ? ids.join(',') : null
}

const usePremiumPlansData = (creatorId: string, enabled: boolean = false): PremiumPlansResult => {
  const locale = useLocale()
  const {
    data: planList = null,
    isLoading: isPlanListLoading,
    error: planListErr,
  } = useSWR(
    enabled && creatorId ? (['getPlanList', creatorId] as const) : null,
    async ([, id]) => {
      const res = await getPlanList(id)
      if (!res) throw new Error('planList request failed')
      const results = res.results as PlanList | null
      return results ?? null
    },
  )

  const planIds = getPlanIds(planList)
  const {
    data: planDetails = null,
    isLoading: isPlanDetailLoading,
    error: planDetailErr,
  } = useSWR(enabled && planIds ? (['getPlanDetail', planIds] as const) : null, async ([, ids]) => {
    const res = await getPlanDetail(ids, locale)
    if (!res) throw new Error('planDetails request failed')
    const results = res.results as PlanDetails | null
    return results ?? null
  })

  const isLoading = enabled ? isPlanListLoading || (!!planIds && isPlanDetailLoading) : false
  const isError = enabled ? Boolean(planListErr || planDetailErr) : false
  const isEmpty = enabled ? !isLoading && !isError && (!planList || !planDetails) : false

  return { planList, planDetails, isLoading, isEmpty, isError }
}
export default usePremiumPlansData
